import { Plus, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useWidth } from "@/components/charts/chart-utils";
import { fmtInput, fmtShort, fmtValue, fmtX, parseAmount, stepBands, stepWord } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { Step } from "@/model/types";

interface Props {
	steps: Step[];
	unit: string;
	scale: "growth" | "level";
	/** The metric's likely range (low / likely / high), shaded on the axis for context. */
	context?: { lo: number; mid: number; hi: number; label: string } | null;
	onChange?: (steps: Step[]) => void;
	onCommit: (steps: Step[]) => void;
	readOnly?: boolean;
	height?: number;
}

type Drag = { kind: "x"; i: number } | { kind: "from"; i: number };

const PAD = { l: 50, r: 14, t: 22, b: 34 };

function sig(v: number): number {
	return v === 0 ? 0 : Number(v.toPrecision(3));
}

/** Round thresholds to 3 significant figures and multipliers to 0.05. */
export function roundSteps(steps: Step[]): Step[] {
	return steps.map((st, i) => ({ ...(i === 0 ? {} : { from: sig(st.from!) }), x: Math.round(st.x * 20) / 20 }));
}

/**
 * A multiplier ladder over a metric's value (PRD §7.4): bands with draggable
 * heights ("times as likely") and draggable thresholds between them.
 */
export function StepLadder({ steps, unit, scale, context, onChange, onCommit, readOnly, height = 170 }: Props) {
	const [wrapRef, W] = useWidth<HTMLDivElement>();
	const svgRef = useRef<SVGSVGElement>(null);
	const [drag, setDrag] = useState<Drag | null>(null);
	const [focus, setFocus] = useState<Drag | null>(null);

	const thresholds = steps.slice(1).map((s) => s.from!);
	const useLog = scale === "growth" && thresholds.every((t) => t > 0) && (!context || context.lo > 0);

	// Domain: every threshold plus the metric's likely range, with room either side.
	const [d0, d1] = useMemo(() => {
		const vals = [...thresholds, ...(context ? [context.lo, context.hi] : [])];
		if (!vals.length) return [0, 1];
		let lo = Math.min(...vals);
		let hi = Math.max(...vals);
		if (useLog) return [lo / 2.5, hi * 2.5];
		const span = hi - lo || Math.max(Math.abs(hi), 1);
		lo -= span * 0.35;
		hi += span * 0.35;
		if (Math.min(...vals) >= 0) lo = Math.max(0, lo);
		return [lo, hi];
		// Domain is fixed while dragging so the axis doesn't move under the pointer.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [drag ? "drag" : JSON.stringify(thresholds), context?.lo, context?.hi, useLog]);

	const yMax = Math.max(2.5, Math.ceil(Math.max(...steps.map((s) => s.x)) * 1.25 * 2) / 2);
	const H = height;
	const iw = W - PAD.l - PAD.r;
	const ih = H - PAD.t - PAD.b;
	const tf = (v: number) => (useLog ? Math.log(v) : v);
	const itf = (v: number) => (useLog ? Math.exp(v) : v);
	const x = (v: number) => PAD.l + ((tf(v) - tf(d0)) / (tf(d1) - tf(d0))) * iw;
	const vAt = (px: number) => itf(tf(d0) + ((px - PAD.l) / iw) * (tf(d1) - tf(d0)));
	const y = (m: number) => PAD.t + (1 - m / yMax) * ih;
	const mAt = (py: number) => (1 - (py - PAD.t) / ih) * yMax;

	const edges = [d0, ...thresholds, d1].map((v, i, a) => (i === 0 ? PAD.l : i === a.length - 1 ? W - PAD.r : x(v)));
	const bands = stepBands(steps, unit);

	function setX(i: number, v: number): Step[] {
		let m = Math.max(0, Math.min(20, v));
		if (Math.abs(m - 1) < 0.06) m = 1;
		return steps.map((st, j) => (j === i ? { ...st, x: Math.round(m * 20) / 20 } : st));
	}
	function setFrom(i: number, v: number): Step[] {
		const lo = i > 1 ? steps[i - 1].from! : -Infinity;
		const hi = i < steps.length - 1 ? steps[i + 1].from! : Infinity;
		const gap = useLog ? 1.02 : (d1 - d0) / 200;
		const clamped = useLog ? Math.min(hi / gap, Math.max(lo * gap, v)) : Math.min(hi - gap, Math.max(lo + gap, v));
		return steps.map((st, j) => (j === i ? { ...st, from: sig(clamped) } : st));
	}

	const local = (e: React.PointerEvent) => {
		const r = svgRef.current!.getBoundingClientRect();
		return { px: e.clientX - r.left, py: e.clientY - r.top };
	};
	function onDown(e: React.PointerEvent, d: Drag) {
		if (readOnly) return;
		e.stopPropagation();
		svgRef.current?.setPointerCapture(e.pointerId);
		setDrag(d);
	}
	function onMove(e: React.PointerEvent) {
		if (!drag) return;
		const { px, py } = local(e);
		onChange?.(drag.kind === "x" ? setX(drag.i, mAt(py)) : setFrom(drag.i, vAt(px)));
	}
	function onUp() {
		if (!drag) return;
		setDrag(null);
		onCommit(roundSteps(steps));
	}
	function onKey(e: React.KeyboardEvent, d: Drag) {
		if (readOnly) return;
		let next: Step[] | null = null;
		if (d.kind === "x" && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
			next = setX(d.i, steps[d.i].x + (e.key === "ArrowUp" ? 1 : -1) * (e.shiftKey ? 0.5 : 0.1));
		} else if (d.kind === "from" && (e.key === "ArrowLeft" || e.key === "ArrowRight")) {
			const dir = e.key === "ArrowRight" ? 1 : -1;
			const v = steps[d.i].from!;
			next = setFrom(d.i, useLog ? v * Math.pow(e.shiftKey ? 1.25 : 1.05, dir) : v + dir * ((d1 - d0) / (e.shiftKey ? 20 : 100)));
		} else return;
		e.preventDefault();
		onCommit(roundSteps(next));
	}

	// Thresholds are typed and shown in the metric's unit: "$25B", "150k", "5%" (review A6).
	const fmtIn = (v: number) => fmtInput(sig(v), unit);
	const parseIn = (s: string) => parseAmount(s, unit) ?? NaN;

	function addStep() {
		const last = steps[steps.length - 1];
		const lastFrom = last.from ?? (useLog ? d0 * 2 : d0);
		const from = useLog ? lastFrom * 2 : lastFrom + (d1 - d0) / 5;
		onCommit(roundSteps([...steps, { from, x: last.x }]));
	}
	function removeStep(i: number) {
		if (steps.length <= 2) return;
		const next = steps.filter((_, j) => j !== i);
		if (i === 0) delete next[0].from;
		onCommit(roundSteps(next));
	}

	const ticks = [0, 1, ...(yMax >= 3 ? [2] : []), ...(yMax >= 5 ? [4] : [])].filter((t) => t <= yMax);

	return (
		<div className="min-w-0 space-y-2">
			<div ref={wrapRef} className="w-full select-none">
				<svg
					ref={svgRef}
					width={W}
					height={H}
					className={cn("block touch-none overflow-visible", drag?.kind === "from" && "cursor-ew-resize")}
					onPointerMove={onMove}
					onPointerUp={onUp}
					role="group"
					aria-label="Multiplier by metric value"
				>
					{ticks.map((t) => (
						<g key={t}>
							<line x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} stroke="var(--grid)" strokeDasharray={t === 1 ? "4 3" : undefined} />
							<text x={PAD.l - 6} y={y(t) + 4} textAnchor="end" className="fill-muted-foreground text-[11px]">
								{t === 1 ? "normal" : fmtX(t)}
							</text>
						</g>
					))}

					{context && (
						<g pointerEvents="none">
							<rect
								x={x(Math.max(d0, context.lo))}
								width={Math.max(2, x(Math.min(d1, context.hi)) - x(Math.max(d0, context.lo)))}
								y={H - PAD.b + 2}
								height={5}
								rx={2}
								fill="var(--model)"
								fillOpacity={0.35}
							/>
							<circle cx={x(Math.min(d1, Math.max(d0, context.mid)))} cy={H - PAD.b + 4.5} r={3.5} fill="var(--model)" />
							<text x={x(Math.min(d1, Math.max(d0, context.mid)))} y={H - 2} textAnchor="middle" className="fill-[var(--model)] text-[11px]">
								{context.label}
							</text>
						</g>
					)}

					{steps.map((st, i) => {
						const x0 = edges[i];
						const x1 = edges[i + 1];
						const active = (drag?.kind === "x" && drag.i === i) || (focus?.kind === "x" && focus.i === i);
						return (
							<g key={i}>
								<rect
									x={x0 + 1.5}
									y={y(st.x)}
									width={Math.max(2, x1 - x0 - 3)}
									height={Math.max(1, H - PAD.b - y(st.x))}
									rx={3}
									fill={st.x < 1 ? "var(--muted-foreground)" : "var(--ink)"}
									fillOpacity={active ? 0.35 : 0.18}
								/>
								<line
									x1={x0 + 3}
									x2={x1 - 3}
									y1={y(st.x)}
									y2={y(st.x)}
									stroke={st.x < 1 ? "var(--muted-foreground)" : "var(--ink)"}
									strokeWidth={active ? 4 : 3}
									strokeLinecap="round"
									tabIndex={readOnly ? -1 : 0}
									role="slider"
									aria-label={`Multiplier for ${bands[i].label}`}
									aria-valuetext={`${bands[i].label}: ${stepWord(st.x)}`}
									className="outline-none"
									onFocus={() => setFocus({ kind: "x", i })}
									onBlur={() => setFocus(null)}
									onKeyDown={(e) => onKey(e, { kind: "x", i })}
								/>
								{!readOnly && (
									<rect
										x={x0 + 3}
										width={Math.max(2, x1 - x0 - 6)}
										y={y(st.x) - 10}
										height={20}
										fill="transparent"
										className="cursor-ns-resize"
										onPointerDown={(e) => onDown(e, { kind: "x", i })}
									/>
								)}
								<text x={(x0 + x1) / 2} y={y(st.x) - 7} textAnchor="middle" className="pointer-events-none fill-foreground text-[11px] font-medium">
									{st.x === 0 ? "never" : st.x === 1 ? "normal" : fmtX(st.x)}
								</text>
							</g>
						);
					})}

					{thresholds.map((v, k) => {
						const i = k + 1;
						const px = x(v);
						const active = (drag?.kind === "from" && drag.i === i) || (focus?.kind === "from" && focus.i === i);
						return (
							<g key={`t${i}`}>
								<line x1={px} x2={px} y1={PAD.t - 4} y2={H - PAD.b} stroke="var(--ink)" strokeOpacity={active ? 0.8 : 0.35} strokeWidth={active ? 2 : 1} />
								<text
									x={px}
									y={H - PAD.b + 18}
									textAnchor="middle"
									className={cn("text-[11px]", active ? "fill-foreground font-medium" : "fill-muted-foreground")}
								>
									{fmtShort(v, unit)}
								</text>
								{!readOnly && (
									<rect
										x={px - 9}
										width={18}
										y={PAD.t - 4}
										height={H - PAD.b - PAD.t + 4}
										fill="transparent"
										className="cursor-ew-resize outline-none"
										tabIndex={0}
										role="slider"
										aria-label={`Threshold ${k + 1}`}
										aria-valuetext={fmtValue(v, unit)}
										onFocus={() => setFocus({ kind: "from", i })}
										onBlur={() => setFocus(null)}
										onKeyDown={(e) => onKey(e, { kind: "from", i })}
										onPointerDown={(e) => onDown(e, { kind: "from", i })}
									/>
								)}
							</g>
						);
					})}
				</svg>
			</div>

			<div className="space-y-1.5 text-sm">
				{steps.map((st, i) => (
					<div key={i} className="flex items-center gap-2">
						<span className="w-10 shrink-0 text-xs text-muted-foreground">{i === 0 ? "Under" : "From"}</span>
						{i === 0 ? (
							<span className="w-28 shrink-0 px-2.5 tabular-nums">{steps[1]?.from !== undefined ? fmtInput(steps[1].from, unit) : "–"}</span>
						) : (
							<Input
								key={`${i}:${st.from}`}
								className="h-8 w-28 shrink-0 tabular-nums"
								defaultValue={fmtIn(st.from!)}
								disabled={readOnly}
								inputMode="decimal"
								aria-label={`Step ${i + 1} starts at`}
								onBlur={(e) => {
									const v = parseIn(e.target.value);
									if (Number.isFinite(v)) onCommit(roundSteps(setFrom(i, v)));
								}}
								onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
							/>
						)}
						<span className="text-xs text-muted-foreground">→</span>
						<Input
							key={`x${i}:${st.x}`}
							className="h-8 w-16 shrink-0"
							defaultValue={String(st.x)}
							disabled={readOnly}
							aria-label={`Step ${i + 1}: times as likely`}
							onBlur={(e) => {
								const v = parseFloat(e.target.value);
								if (Number.isFinite(v)) onCommit(roundSteps(setX(i, v)));
							}}
							onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
						/>
						<span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">× · {stepWord(st.x)}</span>
						{!readOnly && steps.length > 2 && (
							<Button variant="ghost" size="icon" className="size-7" onClick={() => removeStep(i)} aria-label={`Remove step ${i + 1}`} title="Remove step">
								<X className="size-3.5" />
							</Button>
						)}
					</div>
				))}
				{!readOnly && steps.length < 8 && (
					<Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={addStep}>
						<Plus className="size-3.5" /> Add step
					</Button>
				)}
			</div>
		</div>
	);
}
